import React, { useState, useMemo } from 'react';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  Clock, MapPin, User, Phone 
} from 'lucide-react';
import { Button } from './ui/button';

// German month names
const MONTHS_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const WEEKDAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const FollowUpCalendar = ({ followUps = [], onSelectFollowUp, onDateSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Get current month/year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Parse follow-ups into a map by date
  const followUpsByDate = useMemo(() => {
    const map = {};
    followUps.forEach(fu => {
      if (fu.follow_up_date) {
        const dateKey = fu.follow_up_date.split('T')[0]; // YYYY-MM-DD
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(fu);
      }
    });
    return map;
  }, [followUps]);
  
  // Generate calendar days
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get day of week for first day (0 = Sunday, we want Monday = 0)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(currentYear, currentMonth - 1, day);
      days.push({
        date,
        day,
        isCurrentMonth: false,
        dateKey: date.toISOString().split('T')[0]
      });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      days.push({
        date,
        day,
        isCurrentMonth: true,
        dateKey: date.toISOString().split('T')[0]
      });
    }
    
    // Next month days to fill the grid
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(currentYear, currentMonth + 1, day);
      days.push({
        date,
        day,
        isCurrentMonth: false,
        dateKey: date.toISOString().split('T')[0]
      });
    }
    
    return days;
  }, [currentMonth, currentYear]);
  
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };
  
  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };
  
  const handleDayClick = (dayInfo) => {
    setSelectedDate(dayInfo.dateKey);
    if (onDateSelect) {
      onDateSelect(dayInfo.dateKey, followUpsByDate[dayInfo.dateKey] || []);
    }
  };
  
  const isToday = (dateKey) => {
    return dateKey === new Date().toISOString().split('T')[0];
  };
  
  const selectedFollowUps = selectedDate ? (followUpsByDate[selectedDate] || []) : [];
  
  // Count upcoming follow-ups
  const upcomingCount = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return Object.entries(followUpsByDate)
      .filter(([date]) => date >= today)
      .reduce((sum, [, fus]) => sum + fus.length, 0);
  }, [followUpsByDate]);
  
  const overdueCount = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return Object.entries(followUpsByDate)
      .filter(([date]) => date < today)
      .reduce((sum, [, fus]) => sum + fus.length, 0);
  }, [followUpsByDate]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Calendar Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-brand-green" />
            <h3 className="font-semibold text-lg">Follow-up Kalender</h3>
          </div>
          <div className="flex items-center gap-2">
            {overdueCount > 0 && (
              <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                {overdueCount} überfällig
              </span>
            )}
            {upcomingCount > 0 && (
              <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium">
                {upcomingCount} geplant
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handlePrevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium text-lg min-w-[150px] text-center">
              {MONTHS_DE[currentMonth]} {currentYear}
            </span>
            <Button variant="ghost" size="sm" onClick={handleNextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={handleToday}>
            Heute
          </Button>
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="p-4">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS_DE.map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((dayInfo, idx) => {
            const hasFollowUps = followUpsByDate[dayInfo.dateKey]?.length > 0;
            const followUpCount = followUpsByDate[dayInfo.dateKey]?.length || 0;
            const isSelected = selectedDate === dayInfo.dateKey;
            const todayClass = isToday(dayInfo.dateKey);
            const isOverdue = dayInfo.dateKey < new Date().toISOString().split('T')[0] && hasFollowUps;
            
            return (
              <button
                key={idx}
                onClick={() => handleDayClick(dayInfo)}
                className={`
                  relative aspect-square p-1 rounded-lg transition-all text-sm
                  ${!dayInfo.isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                  ${todayClass ? 'bg-brand-green/10 font-bold text-brand-green' : ''}
                  ${isSelected ? 'ring-2 ring-brand-green bg-brand-green/5' : ''}
                  ${hasFollowUps && !isSelected ? 'bg-yellow-50' : ''}
                  ${isOverdue ? 'bg-red-50' : ''}
                  hover:bg-gray-50
                `}
              >
                <span className="block">{dayInfo.day}</span>
                {hasFollowUps && (
                  <div className={`
                    absolute bottom-1 left-1/2 -translate-x-1/2 
                    w-5 h-5 rounded-full text-xs font-medium flex items-center justify-center
                    ${isOverdue ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'}
                  `}>
                    {followUpCount}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Selected Day Detail */}
      {selectedDate && (
        <div className="border-t border-gray-100 p-4">
          <h4 className="font-medium text-gray-700 mb-3">
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('de-DE', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </h4>
          
          {selectedFollowUps.length === 0 ? (
            <p className="text-gray-400 text-sm">Keine Follow-ups an diesem Tag</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedFollowUps.map((fu, idx) => (
                <button
                  key={idx}
                  onClick={() => onSelectFollowUp && onSelectFollowUp(fu)}
                  className="w-full text-left p-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{fu.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {fu.address?.split(',')[0]}
                    </span>
                    {fu.contact_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {fu.contact_phone}
                      </span>
                    )}
                  </div>
                  {fu.follow_up_notes && (
                    <p className="text-xs text-yellow-700 mt-1 line-clamp-2">
                      {fu.follow_up_notes}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FollowUpCalendar;
